import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, Phone, Clock, Wrench, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { api } from "@/lib/api";

export default function Navbar({ company }) {
  const [open, setOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const [services, setServices] = useState([]);
  const phone = company.phone || company.phone_emergency;

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data || [])).catch(() => {});
  }, []);

  const slugOf = (s) => s.slug || s.id;

  return (
    <header className="sticky top-0 z-50">
      {/* Top info strip */}
      <div className="bg-[#E5E7EB] text-[#E11D2A] text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 font-mono">
            <Clock className="w-3.5 h-3.5 text-[#E11D2A]" />
            <span>{company.program_weekdays}</span>
          </div>
          <a
            href={`tel:${phone?.replace(/\s/g, "")}`}
            data-testid="nav-emergency-phone-button"
            className="flex items-center gap-2 font-semibold text-[#E11D2A] hover:text-[#C01820] transition-colors duration-200"
          >
            <Phone className="w-3.5 h-3.5 text-[#E11D2A]" />
            <span className="font-mono">{phone}</span>
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div className="backdrop-blur-xl bg-white/95 border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-2.5 group">
              <span className="font-display font-extrabold text-2xl tracking-tight text-[#E11D2A]">
                SIM<span className="text-[#E11D2A]">SERV</span>
              </span>
            </Link>
            <Link to="/angajati" data-testid="nav-admin-login-button" className="hidden lg:inline-flex">
              <Button variant="outline" className="border-[#E2E8F0] text-[#0F1E36] font-semibold">
                Zona angajați
              </Button>
            </Link>
          </div>

          <nav className="hidden lg:flex items-center gap-8">
            <Link to="/" data-testid="nav-home-link" className="text-sm font-semibold text-slate-600 hover:text-[#FF5A1F] transition-colors duration-200">
              Acasă
            </Link>

            {/* Servicii dropdown */}
            <div className="relative group" data-testid="nav-services-dropdown">
              <Link
                to="/servicii"
                data-testid="nav-services-link"
                className="flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-[#FF5A1F] transition-colors duration-200"
              >
                Servicii
                <ChevronDown className="w-4 h-4 group-hover:rotate-180 transition-transform duration-200" />
              </Link>
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 hidden group-hover:block">
                <div className="w-72 bg-white rounded-xl shadow-xl border border-[#E2E8F0] p-2">
                  {services.map((s) => (
                    <Link
                      key={s.id}
                      to={`/servicii/${slugOf(s)}`}
                      data-testid={`nav-service-${slugOf(s)}`}
                      className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold text-slate-700 hover:bg-[#F4F6F9] hover:text-[#FF5A1F] transition-colors duration-200"
                    >
                      {s.title}
                      <ChevronRight className="w-4 h-4 opacity-40" />
                    </Link>
                  ))}
                  <Link
                    to="/servicii"
                    className="block mt-1 px-3 py-2.5 rounded-lg text-sm font-bold text-[#FF5A1F] border-t border-[#E2E8F0] hover:bg-[#FFF4EF] transition-colors duration-200"
                  >
                    Toate serviciile →
                  </Link>
                </div>
              </div>
            </div>

            <Link to="/despre" data-testid="nav-about-link" className="text-sm font-semibold text-slate-600 hover:text-[#FF5A1F] transition-colors duration-200">Despre Noi</Link>
            <a href="/#cifre" data-testid="nav-stats-link" className="text-sm font-semibold text-slate-600 hover:text-[#FF5A1F] transition-colors duration-200">Cifre</a>
            <Link to="/articole" data-testid="nav-articles-link" className="text-sm font-semibold text-slate-600 hover:text-[#FF5A1F] transition-colors duration-200">Articole</Link>
            <a href="/#contact" data-testid="nav-contact-link" className="text-sm font-semibold text-slate-600 hover:text-[#FF5A1F] transition-colors duration-200">Contact</a>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a href={`tel:${phone?.replace(/\s/g, "")}`}>
              <Button className="bg-[#E11D2A] hover:bg-[#C01820] text-white font-semibold shadow-sm">
                <Phone className="w-4 h-4 mr-2" />
                Sună acum
              </Button>
            </a>
          </div>

          {/* Mobile */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="nav-mobile-menu-button">
                <Menu className="w-6 h-6 text-[#0F1E36]" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-white overflow-y-auto">
              <div className="flex items-center gap-2 mb-8 mt-2">
                <span className="w-8 h-8 rounded-lg bg-[#FF5A1F] flex items-center justify-center">
                  <Wrench className="w-4 h-4 text-white" />
                </span>
                <span className="font-display font-extrabold text-[#0F1E36]">SIMSERV</span>
              </div>
              <nav className="flex flex-col gap-1">
                <Link to="/" onClick={() => setOpen(false)} className="py-3 px-2 rounded-lg text-base font-semibold text-slate-700 hover:bg-[#F4F6F9] transition-colors duration-200">Acasă</Link>

                {/* Servicii expandable */}
                <button
                  onClick={() => setSvcOpen((v) => !v)}
                  data-testid="nav-mobile-services-toggle"
                  className="flex items-center justify-between py-3 px-2 rounded-lg text-base font-semibold text-slate-700 hover:bg-[#F4F6F9] transition-colors duration-200"
                >
                  Servicii
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${svcOpen ? "rotate-180" : ""}`} />
                </button>
                {svcOpen && (
                  <div className="flex flex-col pl-3 border-l-2 border-[#E2E8F0] ml-2">
                    {services.map((s) => (
                      <Link
                        key={s.id}
                        to={`/servicii/${slugOf(s)}`}
                        onClick={() => setOpen(false)}
                        data-testid={`nav-mobile-service-${slugOf(s)}`}
                        className="py-2.5 px-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-[#F4F6F9] hover:text-[#FF5A1F] transition-colors duration-200"
                      >
                        {s.title}
                      </Link>
                    ))}
                    <Link to="/servicii" onClick={() => setOpen(false)} className="py-2.5 px-2 rounded-lg text-sm font-bold text-[#FF5A1F]">
                      Toate serviciile →
                    </Link>
                  </div>
                )}

                <Link to="/despre" onClick={() => setOpen(false)} className="py-3 px-2 rounded-lg text-base font-semibold text-slate-700 hover:bg-[#F4F6F9] transition-colors duration-200">Despre Noi</Link>
                <a href="/#cifre" onClick={() => setOpen(false)} className="py-3 px-2 rounded-lg text-base font-semibold text-slate-700 hover:bg-[#F4F6F9] transition-colors duration-200">Cifre</a>
                <Link to="/articole" onClick={() => setOpen(false)} className="py-3 px-2 rounded-lg text-base font-semibold text-slate-700 hover:bg-[#F4F6F9] transition-colors duration-200">Articole</Link>
                <a href="/#contact" onClick={() => setOpen(false)} className="py-3 px-2 rounded-lg text-base font-semibold text-slate-700 hover:bg-[#F4F6F9] transition-colors duration-200">Contact</a>
              </nav>
              <div className="mt-6 flex flex-col gap-3">
                <a href={`tel:${phone?.replace(/\s/g, "")}`} onClick={() => setOpen(false)}>
                  <Button className="w-full bg-[#FF5A1F] hover:bg-[#E04B14] text-white font-semibold">
                    <Phone className="w-4 h-4 mr-2" /> Sună acum
                  </Button>
                </a>
                <Link to="/angajati" onClick={() => setOpen(false)}>
                  <Button variant="outline" className="w-full border-[#E2E8F0]">Zona angajați</Button>
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <span id="top" />
    </header>
  );
}
